import { QueryKey, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";

export const POLLING_IN_MS = 2000;

export type Config<PromiseType> = Omit<
  UseQueryOptions<PromiseType, AxiosError<unknown, any>, PromiseType, QueryKey>,
  "initialData" | "queryFn" | "queryKey" | "enabled"
> & { initialData?: (() => undefined) | undefined };

const useQueryPollUntilCondition = <PromiseType>(
  api: () => Promise<PromiseType>,
  keys: string[],
  enabled = false, // setting this to false will immediately stop query
  initialInterval = POLLING_IN_MS,
  config?: Config<PromiseType>,
) => {
  const [intervalMs, setIntervalMs] = useState<number>(initialInterval);
  const { status, data, isRefetchError, isFetching, isError, error } = useQuery<
    PromiseType,
    AxiosError,
    PromiseType
  >(
    keys,
    async () => {
      const data = await api();
      if (!enabled) setIntervalMs(0); // equivalent to setting the interval to false
      return data;
    },
    {
      refetchInterval: intervalMs,
      enabled,
      ...config,
    },
  );

  return {
    data,
    hasError: isRefetchError || isError,
    error,
    isFetching,
    status,
  };
};

export default useQueryPollUntilCondition;
